import { useMemo, useState, useEffect, useCallback } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { TbPlus, TbEdit, TbTrash, TbSearch } from "react-icons/tb";
import * as Label from "@radix-ui/react-label";
import toast from "react-hot-toast";
import { RBACService } from "@/services/rbacService";
import { usePermissions } from "@/hooks/usePermissions";
import { useTenant } from "@/hooks/useTenant";
import type {
  Permission,
  Role,
  CreateRoleData,
  UpdateRoleData,
} from "@/types/rbac";
import { ConfirmationModal } from "@/components/ui";

interface RoleManagementProps {
  onDeleteRole?: (roleId: string) => void;
  onFormStateChange?: (isFormOpen: boolean, editingRole: Role | null) => void;
  onBackRef?: React.MutableRefObject<(() => void) | undefined>;
}

export const RoleManagement: React.FC<RoleManagementProps> = ({
  onDeleteRole,
  onFormStateChange,
  onBackRef,
}) => {
  const queryClient = useQueryClient();
  const { tenantId } = useTenant();
  const {
    hasPermission,
    hasRole,
    isLoading: permissionsLoading,
  } = usePermissions();

  const canViewRoles = hasPermission("view_roles") || hasRole("Super Admin");
  const canManageRoles =
    hasPermission("manage_roles") || hasRole("Super Admin");
  const canDeleteRoles =
    hasPermission("delete_roles") || hasRole("Super Admin");

  const [searchTerm, setSearchTerm] = useState("");
  const [showRoleForm, setShowRoleForm] = useState(false);
  const [editingRole, setEditingRole] = useState<Role | null>(null);
  const [roleForm, setRoleForm] = useState({
    name: "",
    description: "",
  });
  const [selectedPermissionIds, setSelectedPermissionIds] = useState<string[]>(
    []
  );
  const [deleteRoleDialog, setDeleteRoleDialog] = useState<{
    open: boolean;
    roleId: string | null;
  }>({ open: false, roleId: null });

  const { data: roles = [], isLoading: rolesLoading } = useQuery({
    queryKey: ["roles", tenantId],
    queryFn: () => RBACService.getRoles(tenantId!),
    enabled: canViewRoles && !!tenantId,
  });

  const { data: permissions = [], isLoading: permissionsQueryLoading } =
    useQuery({
      queryKey: ["permissions", tenantId],
      queryFn: () => RBACService.getPermissions(),
      enabled: canViewRoles && !!tenantId,
    });

  const createRoleMutation = useMutation({
    mutationFn: (roleData: CreateRoleData) =>
      RBACService.createRole(roleData, tenantId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles", tenantId] });
      queryClient.invalidateQueries({ queryKey: ["permissions"] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success("Role created successfully");
      resetRoleForm();
    },
    onError: (error: Error) => {
      toast.error(`Failed to create role: ${error.message}`);
    },
  });

  const updateRoleMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateRoleData }) =>
      RBACService.updateRole(id, data, tenantId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles", tenantId] });
      queryClient.invalidateQueries({ queryKey: ["permissions"] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success("Role updated successfully");
      resetRoleForm();
    },
    onError: (error: Error) => {
      toast.error(`Failed to update role: ${error.message}`);
    },
  });

  const deleteRoleMutation = useMutation({
    mutationFn: (id: string) => RBACService.deleteRole(id, tenantId!),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["roles", tenantId] });
      queryClient.invalidateQueries({ queryKey: ["permissions"] });
      queryClient.invalidateQueries({ queryKey: ["users"] });
      toast.success("Role deleted successfully");
    },
    onError: (error: Error) => {
      toast.error(`Failed to delete role: ${error.message}`);
    },
  });

  const resetRoleForm = useCallback(() => {
    setRoleForm({ name: "", description: "" });
    setSelectedPermissionIds([]);
    setEditingRole(null);
    setShowRoleForm(false);
    if (onFormStateChange) {
      onFormStateChange(false, null);
    }
  }, [onFormStateChange]);

  const handleBack = useCallback(() => {
    resetRoleForm();
  }, [resetRoleForm]);

  // Expose handleBack to parent via ref
  useEffect(() => {
    if (onBackRef) {
      onBackRef.current = handleBack;
    }
  }, [onBackRef, handleBack]);

  const handleCreateRole = () => {
    if (!canManageRoles) {
      toast.error("You do not have permission to manage roles");
      return;
    }

    if (!roleForm.name.trim()) {
      toast.error("Role name is required");
      return;
    }

    if (roleForm.name.trim().length > 50) {
      toast.error("Role name must be 50 characters or less");
      return;
    }

    createRoleMutation.mutate({
      ...roleForm,
      permission_ids: selectedPermissionIds,
    });
  };

  const handleUpdateRole = () => {
    if (!canManageRoles || !editingRole) return;

    if (!roleForm.name.trim()) {
      toast.error("Role name is required");
      return;
    }

    if (roleForm.name.trim().length > 50) {
      toast.error("Role name must be 50 characters or less");
      return;
    }

    updateRoleMutation.mutate({
      id: editingRole.id,
      data: {
        ...roleForm,
        permission_ids: selectedPermissionIds,
      },
    });
  };

  const handleEditRole = async (role: Role) => {
    if (!canManageRoles) {
      toast.error("You do not have permission to manage roles");
      return;
    }

    // Update parent state immediately for instant UI feedback
    if (onFormStateChange) {
      onFormStateChange(true, role);
    }

    setEditingRole(role);
    setRoleForm({
      name: role.name,
      description: role.description || "",
    });
    setShowRoleForm(true);

    const roleWithPermissions = await RBACService.getRoleWithPermissions(
      role.id,
      tenantId!
    );
    setSelectedPermissionIds(roleWithPermissions.permissions.map((p) => p.id));
  };

  const handleDeleteRole = (id: string) => {
    if (!canDeleteRoles) {
      toast.error("You do not have permission to delete roles");
      return;
    }

    setDeleteRoleDialog({ open: true, roleId: id });
  };

  const confirmDeleteRole = (id: string) => {
    deleteRoleMutation.mutate(id, {
      onSuccess: () => {
        setDeleteRoleDialog({ open: false, roleId: null });
        if (editingRole?.id === id) {
          resetRoleForm();
        }
        if (onDeleteRole) {
          onDeleteRole(id);
        }
      },
    });
  };

  const togglePermission = (permissionId: string) => {
    setSelectedPermissionIds((prev) =>
      prev.includes(permissionId)
        ? prev.filter((id) => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  const permissionsByResource = useMemo(
    () =>
      permissions.reduce(
        (acc, permission) => {
          if (!acc[permission.resource]) {
            acc[permission.resource] = [];
          }
          acc[permission.resource].push(permission);
          return acc;
        },
        {} as Record<string, Permission[]>
      ),
    [permissions]
  );

  const filteredRoles = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return roles;
    return roles.filter((role) => role.name.toLowerCase().includes(term));
  }, [searchTerm, roles]);

  const isLoading =
    permissionsLoading || rolesLoading || permissionsQueryLoading;

  if (!canViewRoles && !permissionsLoading) {
    return (
      <div className="flex items-center justify-center py-8 text-[#828282] font-['Open_Sans'] text-[14px]">
        You do not have permission to view roles.
      </div>
    );
  }

  return (
    <div>
      {/* Full Screen Create/Edit Role Form */}
      {showRoleForm || editingRole ? (
        <>
          <div className="flex flex-col items-start w-full overflow-y-auto min-h-[300px] md:min-h-[400px] max-h-[calc(100vh-250px)] md:max-h-[calc(100vh-380px)] p-4 rounded-[10px] border border-gray-100">
            <div className="space-y-6 mb-8 w-full">
              <div>
                <Label.Root
                  htmlFor="role-name"
                  className="inline-flex gap-1 text-[14px] font-medium text-[#444444] mb-3"
                >
                  Name <span className="text-red-500">*</span>
                </Label.Root>
                <div className="bg-gray-100 rounded-[10px] px-4 py-3">
                  <input
                    id="role-name"
                    type="text"
                    value={roleForm.name}
                    onChange={(e) =>
                      setRoleForm({ ...roleForm, name: e.target.value })
                    }
                    className="bg-transparent text-[14px] text-[#444444] font-medium leading-[1.5] placeholder-[#828282] border-none outline-none w-full"
                    placeholder="Enter role name"
                    maxLength={50}
                  />
                </div>
                <div className="flex justify-end mt-1">
                  <span
                    className={`text-[12px] font-medium ${roleForm.name.length >= 50 ? "text-red-500" : "text-[#828282]"}`}
                  >
                    {roleForm.name.length}/50
                  </span>
                </div>
              </div>

              <div>
                <Label.Root
                  htmlFor="role-description"
                  className="block text-[14px] font-medium text-[#444444] mb-3"
                >
                  Description
                </Label.Root>
                <div className="bg-gray-100 rounded-[10px] px-4 py-3">
                  <input
                    id="role-description"
                    type="text"
                    value={roleForm.description}
                    onChange={(e) =>
                      setRoleForm({
                        ...roleForm,
                        description: e.target.value,
                      })
                    }
                    className="bg-transparent text-[14px] text-[#444444] font-medium leading-[1.5] placeholder-[#828282] border-none outline-none w-full"
                    placeholder="Enter description"
                    maxLength={200}
                  />
                </div>
                <div className="flex justify-end mt-1">
                  <span
                    className={`text-[12px] font-medium ${roleForm.description.length >= 200 ? "text-red-500" : "text-[#828282]"}`}
                  >
                    {roleForm.description.length}/200
                  </span>
                </div>
              </div>

              <div>
                <Label.Root className="block text-[14px] font-medium text-[#444444] mb-3">
                  Permissions
                </Label.Root>
                <div className="max-h-[250px] overflow-y-auto p-3 border border-gray-200 rounded-[10px] bg-white">
                  {Object.entries(permissionsByResource).map(
                    ([resource, resourcePermissions]) => (
                      <div key={resource} className="mb-4 last:mb-0">
                        <h6 className="mb-2 font-semibold text-[#444444] font-['Open_Sans'] text-[13px] capitalize">
                          {resource.replace(/_/g, " ")}
                        </h6>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          {resourcePermissions.map((permission) => (
                            <label
                              key={permission.id}
                              className={`flex items-center gap-2 p-2 rounded transition-colors ${canManageRoles ? "hover:bg-gray-50 cursor-pointer" : "cursor-not-allowed opacity-60"}`}
                            >
                              <input
                                type="checkbox"
                                checked={selectedPermissionIds.includes(
                                  permission.id
                                )}
                                onChange={() =>
                                  canManageRoles &&
                                  togglePermission(permission.id)
                                }
                                className="w-4 h-4 text-[#374795] border-gray-300 rounded focus:ring-[#374795]"
                                disabled={!canManageRoles}
                              />
                              <span className="text-[12px] text-[#444444] font-['Open_Sans']">
                                {permission.action.replace(/_/g, " ")}
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                    )
                  )}
                </div>
              </div>
            </div>
          </div>
          <div className="flex gap-6 w-full">
            <button
              onClick={editingRole ? handleUpdateRole : handleCreateRole}
              className="self-stretch w-full px-6 py-3 rounded-[10px] gap-2.5 text-white bg-[#374795] hover:bg-[#132477] cursor-pointer text-sm font-semibold font-['Open_Sans'] leading-tight"
            >
              {editingRole ? "Update Role" : "Create New Role"}
            </button>
          </div>
        </>
      ) : (
        <>
          <div className="flex flex-col items-start w-full overflow-y-auto min-h-[300px] md:min-h-[400px] max-h-[calc(100vh-250px)] md:max-h-[calc(100vh-380px)] p-4 rounded-[10px] border border-gray-100">
            <div className="space-y-4 w-full">
              {/* Search Bar */}
              <div className="flex flex-col items-start gap-[6px] w-full rounded-lg">
                <div className="flex p-[13px_14px] items-center gap-4 w-full rounded-[10px] bg-[#F3F4F6]">
                  <div className="flex justify-center items-center gap-2 flex-1">
                    <TbSearch className="w-4 h-4 text-[#828282]" />
                    <input
                      type="text"
                      placeholder="Search roles"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="bg-transparent border-none outline-none text-[#828282] font-['Open_Sans'] text-[12px] font-medium leading-[18px] flex-1 placeholder:text-[#828282]"
                    />
                  </div>
                </div>
              </div>

              {isLoading ? (
                <div className="w-full flex justify-center py-10">
                  <div className="w-8 h-8 border-4 border-[#374795] border-t-transparent rounded-full animate-spin" />
                </div>
              ) : (
                <div className="space-y-4">
                  {filteredRoles.length === 0 ? (
                    <div className="text-center py-8 text-[#828282] font-['Open_Sans'] text-[14px]">
                      {searchTerm
                        ? "No roles found matching your search."
                        : "No roles created yet. Create your first role!"}
                    </div>
                  ) : (
                    filteredRoles.map((role) => (
                      <div
                        key={role.id}
                        className="bg-white rounded-[12px] p-4 border border-gray-100 shadow-sm"
                      >
                        <div className="flex justify-between items-start gap-4">
                          <div className="flex flex-col gap-1.5">
                            <div className="flex items-center gap-2">
                              <h4 className="font-semibold text-[#1F2937] text-[15px] font-['Open_Sans'] leading-[1.4]">
                                {role.name}
                              </h4>
                              {role.is_system && (
                                <span className="bg-[rgba(55,71,149,0.08)] text-[#374795] px-2 py-0.5 rounded-[20px] text-[10px] font-bold font-['Open_Sans'] uppercase">
                                  System
                                </span>
                              )}
                            </div>
                            {role.description && (
                              <span className="text-[#4B5563] text-[12px] leading-[1.3] font-medium font-['Open_Sans']">
                                {role.description}
                              </span>
                            )}
                            <span className="text-[#9CA3AF] text-[12px] leading-[1.3] font-medium font-['Open_Sans']">
                              Created{" "}
                              {new Date(role.created_at).toLocaleDateString(
                                "en-GB"
                              )}
                            </span>
                          </div>
                          {canManageRoles && (
                            <div className="flex gap-2">
                              <button
                                onClick={() => handleEditRole(role)}
                                className="w-9 h-9 rounded-full border border-gray-200 bg-white flex items-center cursor-pointer justify-center hover:bg-gray-50 transition-colors"
                                title="Edit Role"
                              >
                                <TbEdit size={14} className="text-[#6B7280]" />
                              </button>
                              {canDeleteRoles && !role.is_system && (
                                <button
                                  onClick={() => handleDeleteRole(role.id)}
                                  className="w-9 h-9 rounded-full border border-gray-200 bg-white flex items-center cursor-pointer justify-center hover:bg-gray-50 transition-colors"
                                  title="Delete Role"
                                >
                                  <TbTrash size={14} className="text-red-400" />
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
          </div>

          {canManageRoles && (
            <div className="flex gap-6 w-full mt-4">
              <button
                onClick={() => {
                  // Update parent state first for instant UI feedback
                  if (onFormStateChange) {
                    onFormStateChange(true, null);
                  }
                  // Reset form fields without calling resetRoleForm (which would set showRoleForm to false)
                  setRoleForm({ name: "", description: "" });
                  setSelectedPermissionIds([]);
                  setEditingRole(null);
                  setShowRoleForm(true);
                }}
                className="self-stretch w-full px-6 py-3 rounded-[10px] gap-2.5 text-white bg-[#374795] hover:bg-[#132477] cursor-pointer text-sm font-semibold font-['Open_Sans'] leading-tight flex items-center justify-center"
              >
                <TbPlus className="w-4 h-4" />
                Add New Role
              </button>
            </div>
          )}
        </>
      )}

      {/* Delete Confirmation Modal */}
      <ConfirmationModal
        open={deleteRoleDialog.open}
        onOpenChange={(open) => setDeleteRoleDialog({ open, roleId: null })}
        title="Delete Role"
        description="Are you sure you want to delete this role? This action cannot be undone."
        onConfirm={() =>
          deleteRoleDialog.roleId && confirmDeleteRole(deleteRoleDialog.roleId)
        }
      />
    </div>
  );
};
